import React, { useEffect, useRef, useCallback, useState } from 'react';

/**
 * Cloudflare Turnstile CAPTCHA widget.
 * Renders the Turnstile challenge and calls `onVerify` with the token.
 * Gracefully degrades after timeout — calls onError so forms aren't blocked.
 */

interface TurnstileProps {
  siteKey: string;
  onVerify: (token: string) => void;
  onExpire?: () => void;
  onError?: () => void;
  theme?: 'light' | 'dark' | 'auto';
  size?: 'normal' | 'compact';
  /** How long (ms) to wait before giving up. Default 8000. */
  timeout?: number;
}

declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement, options: Record<string, any>) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

const TurnstileWidget: React.FC<TurnstileProps> = ({
  siteKey,
  onVerify,
  onExpire,
  onError,
  theme = 'auto',
  size = 'normal',
  timeout = 8000,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const mountedRef = useRef(true);
  const resolvedRef = useRef(false);
  const [timedOut, setTimedOut] = useState(false);

  const renderWidget = useCallback(() => {
    if (!containerRef.current || !window.turnstile || widgetIdRef.current) return;
    try {
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        theme,
        size,
        callback: (token: string) => {
          if (mountedRef.current) {
            resolvedRef.current = true;
            onVerify(token);
          }
        },
        'expired-callback': () => {
          if (mountedRef.current) onExpire?.();
        },
        'error-callback': () => {
          if (mountedRef.current) {
            resolvedRef.current = true;
            onError?.();
          }
        },
      });
    } catch {
      // Render failed — treat as error so form isn't blocked
      if (mountedRef.current) {
        resolvedRef.current = true;
        onError?.();
      }
    }
  }, [siteKey, theme, size, onVerify, onExpire, onError]);

  useEffect(() => {
    mountedRef.current = true;
    resolvedRef.current = false;

    let pollInterval: ReturnType<typeof setInterval> | undefined;
    let sdkTimeoutId: ReturnType<typeof setTimeout> | undefined;
    let mainTimeoutId: ReturnType<typeof setTimeout> | undefined;

    if (window.turnstile) {
      renderWidget();
    } else {
      // Poll for SDK to load
      pollInterval = setInterval(() => {
        if (window.turnstile) {
          clearInterval(pollInterval!);
          renderWidget();
        }
      }, 200);

      // If SDK never loads, give up
      sdkTimeoutId = setTimeout(() => {
        if (pollInterval) clearInterval(pollInterval);
        if (mountedRef.current && !resolvedRef.current) {
          setTimedOut(true);
          onError?.();
        }
      }, timeout);
    }

    // Master timeout: if widget rendered but never resolved, auto-fail gracefully
    mainTimeoutId = setTimeout(() => {
      if (mountedRef.current && !resolvedRef.current) {
        setTimedOut(true);
        onError?.();
      }
    }, timeout);

    return () => {
      if (pollInterval) clearInterval(pollInterval);
      if (sdkTimeoutId) clearTimeout(sdkTimeoutId);
      if (mainTimeoutId) clearTimeout(mainTimeoutId);
      mountedRef.current = false;
      if (widgetIdRef.current && window.turnstile) {
        try { window.turnstile.remove(widgetIdRef.current); } catch { /* ignore */ }
        widgetIdRef.current = null;
      }
    };
  }, [renderWidget, timeout, onError]);

  // Hide widget on timeout — form proceeds without CAPTCHA
  if (timedOut) return null;

  return (
    <div
      ref={containerRef}
      style={{
        display: 'flex',
        justifyContent: 'center',
        minHeight: size === 'compact' ? '120px' : '65px',
      }}
    />
  );
};

export default TurnstileWidget;
